/**
 * M94 — Stage 1 (Grasslands tutorial) Level plans.
 * Shorter than Stage 2 by design; progressive tower unlocks + map ladder.
 */

import {
  bossLevelForOrdinal,
  defaultArcPhases,
  type LevelPlan
} from './levelPlan'

/** Tutorial tower unlock ladder — cumulative allow-lists. */
export const CHAPTER1_TOWERS = {
  fire: ['fire'],
  frost: ['fire', 'frost'],
  cannon: ['fire', 'frost', 'cannon'],
  poison: ['fire', 'frost', 'cannon', 'poison'],
  aa: ['fire', 'frost', 'cannon', 'poison', 'aa'],
  all: ['fire', 'frost', 'cannon', 'poison', 'aa', 'mage']
} as const

export type Chapter1TowerTier = keyof typeof CHAPTER1_TOWERS

/**
 * Progressive map ladder — not the same board 15×.
 * Early: single-lane teaching maps; mid+: multi-route production maps.
 */
export const CHAPTER1_MAP_BY_ORDINAL: Record<number, string> = {
  1: 'valley-v',
  2: 'valley-v',
  3: 'switchback-s',
  4: 'valley-v',
  5: 'crossroads-c',
  6: 'switchback-s',
  7: 'rift-r',
  8: 'skew',
  9: 'twinspire',
  10: 'crossroads-c',
  11: 'crossroads-c',
  12: 'switchback-s',
  13: 'twinspire',
  14: 'twinspire',
  15: 'confluence'
}

/** Tower tier introduced / kept for each Level. */
export const CHAPTER1_TOWER_TIER_BY_ORDINAL: Record<number, Chapter1TowerTier> = {
  1: 'fire',
  2: 'frost',
  3: 'frost',
  4: 'cannon',
  5: 'poison',
  6: 'poison',
  7: 'poison',
  8: 'aa',
  9: 'aa',
  10: 'aa',
  11: 'all',
  12: 'all',
  13: 'all',
  14: 'all',
  15: 'all'
}

/**
 * Wave-count targets — early ~5; later Stage 1 ~8–12.
 * Boss Levels (5 / 10 / 15) sit at the high end of their band.
 */
export function chapter1TargetWaveCount(ordinal: number): number {
  const n = Math.max(1, Math.min(15, Math.floor(ordinal)))
  const table: Record<number, number> = {
    1: 5,
    2: 5,
    3: 5,
    4: 5,
    5: 6,
    6: 6,
    7: 7,
    8: 8,
    9: 8,
    10: 9,
    11: 8,
    12: 9,
    13: 10,
    14: 10,
    15: 12
  }
  return table[n]!
}

/** First ordinal that unlocks AA (AIR may appear from here). */
export const CHAPTER1_AA_UNLOCK_ORDINAL = 8

/** First ordinal that unlocks Mage. */
export const CHAPTER1_MAGE_UNLOCK_ORDINAL = 11

export function chapter1TowerTier(ordinal: number): readonly string[] {
  const tier = CHAPTER1_TOWER_TIER_BY_ORDINAL[ordinal] ?? 'all'
  return CHAPTER1_TOWERS[tier]
}

export function chapter1MapId(ordinal: number): string {
  return CHAPTER1_MAP_BY_ORDINAL[ordinal] ?? 'valley-v'
}

/** Stable wave id (`c1s8-w3`). */
export function chapter1WaveId(stageOrdinal: number, waveIndex: number): string {
  return `c1s${stageOrdinal}-w${waveIndex}`
}

/** Build a LevelPlan scaffold for Stage 1 tutorial authoring. */
export function chapter1LevelPlan(
  ordinal: number,
  partial?: Partial<
    Pick<LevelPlan, 'mapId' | 'waveCountTarget' | 'bossLevel' | 'allowedTowerIds' | 'arcPhases'>
  >
): LevelPlan {
  const waveCountTarget = partial?.waveCountTarget ?? chapter1TargetWaveCount(ordinal)
  const bossLevel = partial?.bossLevel ?? bossLevelForOrdinal(ordinal)
  return {
    ordinal,
    mapId: partial?.mapId ?? chapter1MapId(ordinal),
    waveCountTarget,
    bossLevel,
    allowedTowerIds: partial?.allowedTowerIds ?? [...chapter1TowerTier(ordinal)],
    arcPhases: partial?.arcPhases ?? defaultArcPhases(waveCountTarget, bossLevel)
  }
}
